import { AttachmentResponseDto } from '@issuemanager/api-client';
import {
  ControlledFileUploader,
  FileList,
} from '@issuemanager/apps/client/components';
import { useFeatureFlags } from '@issuemanager/client/features/users';
import { IssuePermissionsEnum, IssueStatusEnum } from '@issuemanager/constants';
import { Stack, Typography } from '@mui/material';
import React from 'react';
import { Control, FieldValues, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { EmptyWorkflowState } from '../../components/empty-workflow-state';
import { GroupedDocumentList } from '../../components/grouped-document-list';
import { useGroupedAttachments } from '../../hooks/use-grouped-attachments';
import { useIssueStatusInfo } from '../../hooks/use-issue-status-info';
import { useIssueOutletContext } from '../../hooks/useIssueOutletContext';

type Props<TFieldValues extends FieldValues> = {
  control: Control<TFieldValues>;
  name: Path<TFieldValues>;
  isEditing: boolean;
  attachments: AttachmentResponseDto[];
  isIssueEditable: boolean;
  onEdit: () => void;
};

function canAddAttachmentsToIssue({
  isNotYetAssigned,
  isInProgress,
  isDraftIssue,
  isIssueTypeManager,
  isManager,
  isCreator,
  isIssueTypesExtraFieldsEnabled,
}: {
  isNotYetAssigned: boolean;
  isInProgress: boolean;
  isDraftIssue: boolean;
  isIssueTypeManager?: boolean;
  isManager?: boolean;
  isCreator?: boolean;
  isIssueTypesExtraFieldsEnabled: boolean;
}): boolean {
  if (isNotYetAssigned && isIssueTypeManager) {
    return true;
  }

  if (isInProgress && isManager) {
    return true;
  }

  if (isDraftIssue) {
    if (isCreator) {
      return true;
    }
    if (isIssueTypesExtraFieldsEnabled && (isIssueTypeManager || isManager)) {
      return true;
    }
  }

  return false;
}

export function Documents<TFieldValues extends FieldValues>({
  control,
  name,
  isEditing,
  attachments,
  isIssueEditable,
  onEdit,
}: Props<TFieldValues>): React.JSX.Element {
  const { t } = useTranslation('issue');
  const { issue, userRoles: issueUserRoles } = useIssueOutletContext();
  const isIssueTypesExtraFieldsEnabled = useFeatureFlags(
    'issueTypesExtraFields',
  );
  const { isNotYetAssigned } = useIssueStatusInfo({
    status: issue.data?.status,
    isInitiated: issue.data?.isInitiated,
    approvalStatus: issue.data?.approvalStatus,
  });

  const isInProgress = issue.data?.status === IssueStatusEnum.IN_PROGRESS;
  const isDraftIssue = issue.data?.status === IssueStatusEnum.DRAFT;

  const canAddAttachments = canAddAttachmentsToIssue({
    isNotYetAssigned,
    isInProgress,
    isDraftIssue,
    isIssueTypeManager: issueUserRoles.data?.isIssueTypeManager,
    isManager: issueUserRoles.data?.isManager,
    isCreator: issueUserRoles.data?.isCreator,
    isIssueTypesExtraFieldsEnabled: Boolean(isIssueTypesExtraFieldsEnabled),
  });

  const showFileUploader = isEditing && canAddAttachments;
  const showEmptyState =
    !attachments.length && isIssueEditable && canAddAttachments;

  const { regularAttachments, approvalAttachments } =
    useGroupedAttachments(attachments);

  return (
    <Stack data-testid='documents' spacing={3}>
      <Typography variant='h3' fontWeight='medium'>
        {t('form.sections.documents')}
      </Typography>

      {showFileUploader && (
        <>
          <ControlledFileUploader
            control={control}
            name={name}
            fullWidth
            initialFileList={isInProgress ? [] : attachments}
            permissions={[
              IssuePermissionsEnum.IsIssueTypeManager,
              IssuePermissionsEnum.IsManager,
              IssuePermissionsEnum.IsCreator,
            ]}
          />
          {isInProgress && (
            <FileList files={attachments} showVerticalDividers={false} />
          )}
        </>
      )}

      {!showFileUploader && showEmptyState && (
        <EmptyWorkflowState
          onEdit={onEdit}
          description={t('manage.documents.empty.description')}
        />
      )}

      {!showFileUploader && !showEmptyState && (
        <GroupedDocumentList
          regularAttachments={regularAttachments}
          approvalAttachments={approvalAttachments}
          spacing={1}
        />
      )}
    </Stack>
  );
}
